/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState } from 'react';
import { Modal } from 'blb-components/GrommetComponents';
import { Box, Button, Text } from 'grommet';
import { Field, Form } from 'react-final-form';
import { AlertBar } from '@blend/enterprise-components';
import { getPasswordInputConfig } from 'blb-utils/inputs';
import * as Sentry from '@sentry/browser';
import { get } from 'lodash';

interface FormValues {
  password?: string;
}

export namespace CredentialPasswordModal {
  export interface Props {
    handleClose: () => void;
    handleSubmit: (password: string) => Promise<boolean>;
  }
}

export const CredentialPasswordModal: React.FC<CredentialPasswordModal.Props> = ({ handleClose, handleSubmit }) => {
  const [error, setError] = useState<string | undefined>();
  const initialFormValues: FormValues = { password: '' };
  const onSubmit = async (values: FormValues) => {
    try {
      setError(undefined);
      const validPassword = await handleSubmit(values.password!);
      if (!validPassword) {
        setError('Invalid password');
      } else {
        handleClose();
      }
    } catch (error) {
      Sentry.captureException(error);
      setError(get(error, 'message'));
    }
  };

  return (
    <Form initialValues={initialFormValues} onSubmit={onSubmit}>
      {({ hasValidationErrors, submitting, handleSubmit }) => {
        const submitButton = (
          <Button
            disabled={hasValidationErrors || submitting}
            id="it-submitCredentialPassword"
            label="Upload"
            onClick={handleSubmit}
            primary
            type="submit"
          />
        );
        return (
          <Modal
            buttonComponents={submitButton}
            handleClose={handleClose}
            isOpen={true}
            title={'This file is password protected'}
          >
            <Box alignContent="start" gap="small" width={{ width: '540px', max: '540px' }}>
              {error && <AlertBar id="it-credentialPasswordModalError" message={error} theme="error" />}
              <Text>Please enter the password to upload this file to your profile.</Text>
              <Field {...getPasswordInputConfig()} autoFocus={true} />
            </Box>
          </Modal>
        );
      }}
    </Form>
  );
};
